import type { IMarket } from "~/types/event";
import { formatCents } from "~/utils/formatPrice";
import { formatPercentageValue } from "~/utils/formatValue";

export interface IMarketColumnValue {
  read: (market: IMarket) => number | null;
  round: (value: number) => number;
  format: (value: number | null) => string;
}

const NOT_AVAILABLE = "N/A";

const toWholePercent = (value: number) => Math.round(value * 100) / 100;

const toTenthOfCent = (value: number) =>
  Math.round((value * 100 + Number.EPSILON) * 10) / 1000;

export const chanceColumn: IMarketColumnValue = {
  read: ({ chance }) => chance,
  round: toWholePercent,
  format: (value) =>
    value === null
      ? NOT_AVAILABLE
      : formatPercentageValue(value * 100, { maximumFractionDigits: 1 }),
};

export const spreadColumn: IMarketColumnValue = {
  read: ({ spread }) => spread ?? null,
  round: toTenthOfCent,
  format: (value) => formatCents(value),
};

export const readMarketValue = (
  column: IMarketColumnValue,
  market: IMarket
): number | null => {
  const raw = column.read(market);

  return raw === null || !Number.isFinite(raw) ? null : column.round(raw);
};

// Only a literal 0 is the workers' "confirmed no executed trade" flag (PH-1025, which nulls
// price/chance at the source). null means the provider reported no volume this cycle, which
// says nothing about whether the market has traded.
export const isUntraded = (volume: number | null | undefined): boolean =>
  volume === 0;

export const isPriceUnavailable = (
  volume: number | null | undefined,
  price: number | null | undefined
): boolean => isUntraded(volume) || !Number.isFinite(price);
